// 「どこかへ行きたい」用: 近場の吉方位カードと、行った場所・気分の記録
import React, { useState } from 'react';
import { NearbyResult, PLACE_TYPES, MOODS } from './nearby';
import { updateChoice } from './dailyChoices';

const box: React.CSSProperties = { background: '#f0f9ff', borderRadius: '14px', border: '1px solid #bae6fd', padding: '14px 16px', marginBottom: '14px' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#d97706', fontSize: '14px', cursor: 'pointer', padding: '8px', textDecoration: 'underline' };
const chip = (on: boolean): React.CSSProperties => ({
  minHeight: '44px', padding: '8px 14px', borderRadius: '22px', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit',
  border: on ? '2px solid #d97706' : '1px solid #e5e7eb', background: on ? '#fffbeb' : 'white', color: on ? '#78350f' : '#374151', fontWeight: on ? 'bold' : 'normal',
});

type CardProps = {
  isPremium: boolean;
  hasHonmei: boolean;
  nearby: NearbyResult | null;
  starName: (s: number) => string;
  honmeiName?: string | null;
  onUnlock: () => void;
};

export function NearbyCard({ isPremium, hasHonmei, nearby, starName, honmeiName, onUnlock }: CardProps) {
  const [why, setWhy] = useState(false);
  const title = <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0369a1', marginBottom: '6px' }}>🧭 近場の吉方位（車で20〜30分くらい）</div>;

  if (!isPremium) {
    return (
      <div style={{ ...box, textAlign: 'center' }}>
        {title}
        <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.7 }}>今日の気に合う方角と場所の種類は、プレミアムで見られます。</div>
        <button onClick={onUnlock} style={linkBtn}>プレミアムについて見る</button>
      </div>
    );
  }
  if (!hasHonmei) {
    return (
      <div style={box}>
        {title}
        <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.7 }}>プロフィールに生年月日を登録すると、あなたに合う方角をお伝えできます。</div>
        <button onClick={() => window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'profile' }))} style={linkBtn}>プロフィールを登録する</button>
      </div>
    );
  }
  if (!nearby || nearby.kind === 'none') {
    return (
      <div style={box}>
        {title}
        <div style={{ fontSize: '15px', color: '#0c4a6e', lineHeight: 1.7 }}>今日は方角を気にせず、近くで気持ちのいい場所を選んでみてね。</div>
      </div>
    );
  }
  return (
    <div style={box}>
      {title}
      <div style={{ fontSize: '17px', fontWeight: 'bold', color: '#0c4a6e', lineHeight: 1.6 }}>
        今日は<span style={{ color: '#d97706' }}>{nearby.direction}</span>の方角、{nearby.place}が合いそうです。
      </div>
      <button onClick={() => setWhy(w => !w)} style={{ ...linkBtn, color: '#6b7280', padding: '6px 0' }}>{why ? '閉じる' : 'この見立ての理由'}</button>
      {why && (
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.8 }}>
          今日の日盤では、{nearby.direction}に{starName(nearby.star)}が回っています。
          {honmeiName}から見ると「{nearby.relation}」の関係で、
          {nearby.relation === '生気' ? '力をもらいやすい方角です。' : nearby.relation === '比和' ? 'なじみやすい方角です。' : '穏やかに過ごせる方角です。'}
          五黄殺・暗剣殺・本命殺・本命的殺・日破にあたる方角は外しています。あくまで参考のひとつとしてどうぞ。
        </div>
      )}
    </div>
  );
}

type RecordProps = { rowId: string; onSaved?: (place: string, mood: string) => void };

/** 行った場所の種類とそのときの気分を、判定の行に書き足す（有料・ログイン中のみ表示） */
export function PlaceRecord({ rowId, onSaved }: RecordProps) {
  const [place, setPlace] = useState<string | null>(null);
  const [mood, setMood] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!place || !mood) return;
    await updateChoice(rowId, { place_type: place, mood_after: mood });
    setSaved(true);
    onSaved?.(place, mood);
  };

  if (saved) return <div style={{ fontSize: '14px', color: '#16a34a', textAlign: 'center', padding: '10px' }}>記録しました。振り返りで見られます。</div>;

  return (
    <div style={{ textAlign: 'left', borderTop: '1px solid #f3f4f6', paddingTop: '14px', marginTop: '6px' }}>
      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#78350f', marginBottom: '8px' }}>行ってきたら記録する</div>
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>どんな場所だった？</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
        {PLACE_TYPES.map(p => <button key={p} onClick={() => setPlace(p)} style={chip(place === p)}>{p}</button>)}
      </div>
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>そのときの気分は？</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {MOODS.map(m => <button key={m} onClick={() => setMood(m)} style={chip(mood === m)}>{m}</button>)}
      </div>
      <button onClick={save} disabled={!place || !mood}
        style={{ width: '100%', minHeight: '52px', borderRadius: '14px', border: 'none', fontSize: '17px', fontWeight: 'bold', fontFamily: 'serif', cursor: place && mood ? 'pointer' : 'default',
          background: place && mood ? 'linear-gradient(135deg,#f59e0b,#d97706)' : '#e5e7eb', color: place && mood ? 'white' : '#9ca3af' }}>記録する</button>
      <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '8px' }}>あとから振り返り画面でも記録できます。</div>
    </div>
  );
}
